import { Node, Handle, NodeProps, Position, useNodeId } from "@xyflow/react";
import { Icon, SkeletonText, SkeletonBlock } from "@zapier/design-system";
import classNames from "classnames";
import { useSetAtom, useAtomValue } from "jotai";
import {
  isAcceptingSuggestionStateAtom,
  acceptSuggestionSetter,
  removeSuggestedNodeAndEdgesSetter,
  shouldShowStepIdsAtom,
  SuggestedNodeData,
} from "@zapier/canvas-state";

import { StepId } from "../../../StepId";
import styles from "./SuggestedNode.module.css";

export function SuggestedNode(props: NodeProps<Node<SuggestedNodeData>>) {
  const { data, selected } = props;
  const nodeId = useNodeId();
  const isAcceptingSuggestionState = useAtomValue(
    isAcceptingSuggestionStateAtom,
  );
  const acceptSuggestion = useSetAtom(acceptSuggestionSetter);
  const removeSuggestedNodeAndEdges = useSetAtom(
    removeSuggestedNodeAndEdgesSetter,
  );
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);

  const getIcon = () => {
    if (!data) return null;
    switch (data.suggestedType) {
      case "table":
        return <Icon size={28} color="UiBrand" name="navTables" />;
      case "interface":
        return <Icon size={28} color="UiBrand" name="navInterfaces" />;
      case "zap":
        return <Icon size={28} color="UiBrand" name="miscBoltAlt" />;
      default:
        return null;
    }
  };

  const handleAccept = () => {
    if (!nodeId) return;
    acceptSuggestion(nodeId);
  };

  const handleReject = () => {
    if (!nodeId) return;
    removeSuggestedNodeAndEdges(nodeId);
  };

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.wrapperSelected]: selected,
      })}
    >
      {shouldShowStepIds && <StepId />}
      <Handle
        id="left"
        type="target"
        position={Position.Left}
        className={styles.handle}
      />
      <Handle
        id="right"
        type="source"
        position={Position.Right}
        className={styles.handle}
      />
      {!data.label ? (
        <div className={styles.content}>
          <SkeletonBlock borderRadius="small" height="28px" width="28px" />
          <SkeletonText
            lines={1}
            size="heading"
            textAlign="left"
            width="100%"
          />
        </div>
      ) : (
        <>
          <div className={classNames(styles.actionPill)}>
            {isAcceptingSuggestionState ? (
              <div className={styles.spinnerWrapper}>
                <div className={styles.spinnerWrapper}>
                  <div className={styles.customSpinner} />
                </div>
              </div>
            ) : (
              <>
                <button
                  className={classNames(styles.pillButton, styles.acceptButton)}
                  onClick={data.zsl ? handleAccept : undefined}
                  title={
                    !data.zsl ? "Loading suggestion..." : "Accept suggestion"
                  }
                  disabled={isAcceptingSuggestionState}
                >
                  {!data.zsl ? (
                    <div className={styles.spinnerWrapper}>
                      <div className={styles.customSpinner} />
                    </div>
                  ) : (
                    <div className={styles.iconWrapper}>
                      <Icon
                        name="formCheck"
                        size={16}
                        color="BrandAlmostWhite"
                      />
                    </div>
                  )}
                </button>
                <div className={styles.pillDivider} />
                <button
                  className={classNames(styles.pillButton, styles.rejectButton)}
                  onClick={handleReject}
                  title="Reject suggestion"
                  disabled={isAcceptingSuggestionState}
                >
                  <div className={styles.iconWrapper}>
                    <Icon name="formX" size={16} color="BrandAlmostWhite" />
                  </div>
                </button>
              </>
            )}
          </div>
          <div className={styles.content}>
            {getIcon()}
            <span className={styles.label}>{data?.label}</span>
          </div>
        </>
      )}
    </div>
  );
}
